/**
 * ProfileScreen - Profile display and switching
 * Shows current child profile with option to switch to other profiles
 */

import { t } from '../i18n/config.js';
import { api } from '../services/api.js';
import { startHeartbeat } from '../app-new.js';

export class ProfileScreen {
  constructor(container, router, profileData, navigationBar) {
    this.container = container;
    this.router = router;
    this.profileData = profileData;
    this.navigationBar = navigationBar;
    this.familyProfiles = [];
  }

  /**
   * Render profile screen
   */
  async render() {
    // Clear container
    this.container.innerHTML = '';

    // Show loading state
    this.container.innerHTML = `
      <div class="profile-screen loading"></div>
    `;

    // Show Kassie loader
    window.showLoader();

    try {
      // Load family profiles
      await this.loadFamilyProfiles();

      // Render profile interface
      this.renderProfileInterface();
    } catch (error) {
      console.error('Error loading profile screen:', error);
      this.renderError();
    }
  }

  /**
   * Load family profiles
   */
  async loadFamilyProfiles() {
    try {
      // Get family ID from authorized family in localStorage
      const authorizedFamily = localStorage.getItem('authorizedFamily');
      if (!authorizedFamily) {
        console.warn('No authorized family found in localStorage');
        this.familyProfiles = this.profileData ? [this.profileData] : [];
        return;
      }

      const family = JSON.parse(authorizedFamily);
      const familyId = family.id;

      console.log('📋 Loading profiles for family:', familyId);

      // Load profiles from API
      const { getDeviceId } = await import('../services/deviceAuth.js');
      const deviceId = getDeviceId();

      const response = await api.get(`/api/device/families/${familyId}/profiles`, {
        deviceId: deviceId,
      });

      const profiles = response.profiles || response || [];
      this.familyProfiles = profiles;

      console.log('📋 Loaded family profiles:', profiles.length);
    } catch (error) {
      console.error('Failed to load family profiles:', error);
      // Fallback: show current profile if available
      this.familyProfiles = this.profileData ? [this.profileData] : [];
    }
  }

  /**
   * Render profile interface
   */
  renderProfileInterface() {
    console.log('📋 Rendering profile interface');
    console.log('Current profile:', this.profileData);
    console.log('All family profiles:', this.familyProfiles);

    // Hide Kassie loader
    window.hideLoader();

    // Separate profiles into children and adults
    const childProfiles = this.familyProfiles.filter((p) => p.type !== 'adult');
    const adultProfiles = this.familyProfiles.filter((p) => p.type === 'adult');

    this.container.innerHTML = `
      <div class="profile-screen">
        <!-- Profile Selection Prompt -->
        <div class="profile-prompt">
          <h1 class="profile-prompt-text" id="profilePromptText">${t('child.profile.whoWantsToLearn') || 'Wie wil speel?'}</h1>
        </div>

        <!-- Child Profile Cards (Row 1) -->
        ${childProfiles.length > 0 ? `
        <div class="profile-cards profile-cards--children">
          ${childProfiles.map((profile) => this.renderProfileCard(profile, this.profileData && profile.id === this.profileData.id)).join('')}
        </div>
        ` : ''}

        <!-- Adult Profile Cards (Row 2) -->
        ${adultProfiles.length > 0 ? `
        <div class="profile-cards profile-cards--adults">
          ${adultProfiles.map((profile) => this.renderProfileCard(profile, this.profileData && profile.id === this.profileData.id)).join('')}
        </div>
        ` : ''}
      </div>
    `;

    // Bind events
    this.bindEvents();

    // Start prompt rotation timer (only if no profile is actively selected)
    if (!this.profileData) {
      this.startPromptRotation();
    }
  }

  /**
   * Render profile card
   */
  renderProfileCard(profile, isCurrent = false) {
    // Construct full avatar URL
    const isAdult = profile.type === 'adult';
    let avatarUrl;
    if (profile.avatarUrl) {
      // If it's just a filename, construct full URL
      if (!profile.avatarUrl.startsWith('http')) {
        avatarUrl = `/svg/components/${profile.avatarUrl}`;
      } else {
        avatarUrl = profile.avatarUrl;
      }
    } else if (profile.avatar) {
      // Fallback to avatar field
      if (!profile.avatar.startsWith('http')) {
        avatarUrl = `/svg/components/${profile.avatar}`;
      } else {
        avatarUrl = profile.avatar;
      }
    } else {
      // Default avatar — different for adults vs children
      avatarUrl = isAdult ? '/svg/components/GrownUp-1.svg' : '/svg/branding/KassieAvatar.svg';
    }

    // Show first name only for adults
    let name = profile.firstName || profile.name || 'Profile';
    if (isAdult) {
      name = name.split(' ')[0]; // First name only
    }

    // Check for name clash between adults and children — append suffix to both
    const firstName = name.split(' ')[0].toLowerCase().trim();
    const childProfiles = this.familyProfiles.filter((p) => p.type !== 'adult');
    const adultProfiles = this.familyProfiles.filter((p) => p.type === 'adult');
    let suffix = '';

    if (isAdult) {
      const clashingChild = childProfiles.find((p) => {
        const childName = (p.name || p.firstName || '').split(' ')[0].toLowerCase().trim();
        return childName === firstName;
      });
      if (clashingChild) {
        const lang = localStorage.getItem('i18nextLng') || 'af';
        suffix = lang === 'af' ? ' <sub class="profile-suffix">(sr)</sub>' : ' <sub class="profile-suffix">(snr)</sub>';
      }
    } else {
      const clashingAdult = adultProfiles.find((p) => {
        const adultName = (p.firstName || p.name || '').split(' ')[0].toLowerCase().trim();
        return adultName === firstName;
      });
      if (clashingAdult) {
        const lang = localStorage.getItem('i18nextLng') || 'af';
        suffix = lang === 'af' ? ' <sub class="profile-suffix">(jr)</sub>' : ' <sub class="profile-suffix">(jnr)</sub>';
      }
    }

    const adultClass = isAdult ? ' profile-card--adult' : '';

    return `
      <div class="profile-card ${isCurrent ? 'profile-card--current' : ''}${adultClass}" data-profile-id="${profile.id}" data-profile-type="${profile.type || 'child'}">
        <div class="profile-card-avatar">
          <div class="profile-card-avatar__frame${isAdult ? ' profile-card-avatar__frame--adult' : ''}">
            <img src="${avatarUrl}" alt="${name}" referrerpolicy="no-referrer" onerror="this.onerror=null;this.src='/svg/components/GrownUp-1.svg'" />
          </div>
          ${
            isCurrent
              ? `
          <div class="profile-card-badge">
            <svg width="36" height="36" viewBox="0 0 36 36">
              <image href="/svg/components/GreenStar.svg" x="6" y="6" width="24" height="24" />
            </svg>
          </div>
          `
              : `
          <div class="profile-card-switch">
            <img src="/svg/buttons/Reload.svg" alt="Switch" />
          </div>
          `
          }
        </div>
        <div class="profile-card-name">${name}</div>
      </div>
    `;
  }

  /**
   * Bind events
   */
  bindEvents() {
    // Profile card click handlers
    const profileCards = this.container.querySelectorAll('.profile-card');
    profileCards.forEach((card) => {
      card.addEventListener('click', () => {
        const profileId = card.dataset.profileId;
        // Allow switching to any profile, including if no profile is currently selected
        if (!this.profileData || profileId !== this.profileData.id) {
          this.switchProfile(profileId);
        }
      });
    });
  }

  /**
   * Switch to different profile
   */
  async switchProfile(profileId) {
    try {
      console.log(`🔄 Switching to profile: ${profileId}`);

      // Stop the prompt rotation once a profile is selected
      this.stopPromptRotation();

      // Find the profile
      const newProfile = this.familyProfiles.find((p) => p.id === profileId);
      if (!newProfile) {
        throw new Error(`Profile not found: ${profileId}`);
      }

      // Show loading state
      this.container.classList.add('loading');

      // Update localStorage
      localStorage.setItem('selectedProfile', JSON.stringify(newProfile));

      // Start background sync for the new profile
      import('../services/backgroundSync.js').then(({ startBackgroundSync }) => {
        startBackgroundSync(newProfile.id);
      });

      // Send heartbeat for the newly selected profile
      startHeartbeat(newProfile.id);

      // Update navigation bar
      if (this.navigationBar) {
        this.navigationBar.updateProfile(newProfile);
      }

      // Update current profile data
      this.profileData = newProfile;

      // Reload profile screen
      await this.render();

      // Navigate to library (content home page) after profile selection
      this.router.navigate('library', newProfile);

      console.log('✅ Profile switched successfully');
    } catch (error) {
      console.error('Failed to switch profile:', error);
      import('../utils/toast.js').then(({ showToast }) => {
        showToast(t('child.errors.profileSwitchFailed') || 'Failed to switch profile. Please try again.', 'error');
      });
      this.container.classList.remove('loading');
    }
  }

  /**
   * Render error state
   */
  renderError() {
    this.container.innerHTML = `
      <div class="profile-screen">
        <div class="profile-empty">
          <div class="profile-empty-icon">😕</div>
          <p class="profile-empty-message">
            ${t('child.errors.couldNotLoadProfile') || 'Could not load profile information.'}
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Destroy view
   */
  destroy() {
    this.stopPromptRotation();
    this.container.innerHTML = '';
  }

  /**
   * Start rotating prompt text between "Wie wil speel?" and "Kies 'n profiel om te speel!"
   */
  startPromptRotation() {
    this.stopPromptRotation();
    this._promptShowingAlt = false;

    this._promptRotationTimer = setInterval(() => {
      const promptEl = document.getElementById('profilePromptText');
      if (!promptEl) {
        this.stopPromptRotation();
        return;
      }

      this._promptShowingAlt = !this._promptShowingAlt;

      if (this._promptShowingAlt) {
        promptEl.textContent = t('child.profile.chooseProfile') || "Kies 'n profiel om te speel!";
        promptEl.classList.add('profile-prompt-wiggle');
      } else {
        promptEl.textContent = t('child.profile.whoWantsToLearn') || 'Wie wil speel?';
        promptEl.classList.remove('profile-prompt-wiggle');
      }
    }, 2000);
  }

  /**
   * Stop prompt rotation timer
   */
  stopPromptRotation() {
    if (this._promptRotationTimer) {
      clearInterval(this._promptRotationTimer);
      this._promptRotationTimer = null;
    }
  }
}


/**
 * Send a heartbeat for the given profile immediately.
 * Called when a child profile is selected/switched.
 * @param {string} profileId
 */
async function sendProfileHeartbeat(profileId) {
  try {
    const deviceId = localStorage.getItem('kassie_device_id');
    const storedFamily = localStorage.getItem('authorizedFamily');
    if (!deviceId || !storedFamily) return;

    const family = JSON.parse(storedFamily);
    const familyId = family.id;
    if (!familyId) return;

    await api.post('/api/auth/device/heartbeat', {
      device_id: deviceId,
      family_id: familyId,
      profile_id: profileId,
    });
  } catch {
    // Offline or error — silently ignore
  }
}
